/* eslint-disable no-template-curly-in-string */
import * as Yup from 'yup';
import valid from 'card-validator';
import {FIXED_FUND} from '../../constants';
import {getNumberFromString} from '../../utils/getAmounts';

const CREDIT_CARD_REGEX = /(\d{4}\s*\d{4}\s*\d{4}\s*\d{4})|(\d{4}\s*\d{6}\s*\d{4})|(\d{16})/;
const SENSITIVE_DATA_WARNING =
  'It looks like you might have entered sensitive information, like a credit card or bank account number, in your gift note. To protect yourself, you should remove any sensitive information before placing your order.';
const MAX_CONTRIBUTION_DOLLARS = 5000;
const MAX_CONTRIBUTION_CENTS = 500000;
const formatNumber = value => value.toLocaleString('en-US');
const MAX_CONTRIBUTION_MESSAGE = `Please enter $${formatNumber(
  MAX_CONTRIBUTION_DOLLARS,
)} or less.`;

export const getInitialValues = quantity => ({
  address: '',
  card: '',
  city: '',
  cvv: '',
  email: '',
  expiration: '',
  isTermsAccepted: false,
  name: '',
  note: '',
  quantity,
  state: '',
  zip: '',
});

const getMaxFixedQuantity = (numRemaining = 0, priceCents = 0) => {
  if (!priceCents || priceCents < 1) {
    return numRemaining;
  }

  return Math.min(
    numRemaining,
    Math.floor(MAX_CONTRIBUTION_CENTS / priceCents),
  );
};

export const getValidationSchema = (
  fundType = '',
  numRemaining = 0,
  priceCents = 0,
) => {
  const isFixedFund = fundType === FIXED_FUND;
  const maxFixedQuantity = getMaxFixedQuantity(numRemaining, priceCents);
  const getQuantityNumber = value =>
    isFixedFund ? value : getNumberFromString(value);

  return Yup.object().shape({
    address: Yup.string()
      .trim()
      .max(100, 'Please limit your billing address to ${max} characters.')
      .required('Please enter a valid billing address.'),
    card: Yup.string()
      .test(
        'card',
        'Please enter a valid credit card number.',
        value => valid.number(value).isValid,
      )
      .required(),
    city: Yup.string()
      .trim()
      .max(100, 'Please limit your city name to ${max} characters.')
      .required('Please enter a valid city.'),
    cvv: Yup.string()
      .test(
        'cvv',
        'Invalid CVV.',
        value => valid.cvv(value, 3).isValid || valid.cvv(value, 4).isValid,
      )
      .required(),
    email: Yup.string()
      .email('Please enter a valid email address.')
      .required(),
    expiration: Yup.string()
      .test(
        'expiration',
        'Invalid expiration.',
        value => valid.expirationDate(value).isValid,
      )
      .required(),
    isTermsAccepted: Yup.bool().oneOf(
      [true],
      'You must read and agree to these terms and conditions.',
    ),
    name: Yup.string()
      .trim()
      .max(100, 'Please limit your name to ${max} characters.')
      .required(
        'Please enter the name exactly as it appears on the credit card.',
      ),
    note: Yup.string()
      .trim()
      .max(500, 'Please limit your message to ${max} characters.')
      .test('note', SENSITIVE_DATA_WARNING, value => {
        const hasSensitiveData = value?.match(CREDIT_CARD_REGEX);
        return !hasSensitiveData;
      }),
    quantity: Yup.string()
      .test(
        'quantityMin',
        isFixedFund ? 'Please enter at least 1.' : 'Please enter at least $1.',
        quantityValue => getQuantityNumber(quantityValue) > 0,
      )
      .test(
        'quantityMax',
        isFixedFund
          ? `Please enter ${formatNumber(maxFixedQuantity)} or less.`
          : MAX_CONTRIBUTION_MESSAGE,
        quantityValue =>
          isFixedFund
            ? getQuantityNumber(quantityValue) <= maxFixedQuantity
            : getQuantityNumber(quantityValue) <= MAX_CONTRIBUTION_DOLLARS,
      )
      .required(),
    state: Yup.string().required('Required.'),
    zip: Yup.string()
      .test(
        'zip',
        'Please enter a valid zip code.',
        value => valid.postalCode(value).isValid,
      )
      .required(),
  });
};
